import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import * as XLSX from "xlsx";
import {
  saveCampaign,
  getAudiencePreviewCount,
  uploadAudienceFile,
  sendCampaignNow,
  scheduleCampaign,
  getVendorTemplates,
} from "../../services/campaignService";

const inputStyle = {
  width: "100%",
  height: "44px",
  border: "1px solid #D1D5DB",
  borderRadius: "8px",
  padding: "0 12px",
  fontSize: "14px",
  color: "#374151",
  outline: "none",
  boxSizing: "border-box",
  background: "#fff",
};

const selectStyle = {
  ...inputStyle,
  paddingRight: "36px",
  appearance: "none",
  backgroundImage:
    "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%236B7280' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E\")",
  backgroundRepeat: "no-repeat",
  backgroundPosition: "right 10px center",
};

const labelStyle = {
  display: "block",
  fontSize: "13px",
  fontWeight: "600",
  color: "#374151",
  marginBottom: "6px",
};

const errorStyle = {
  color: "#EF4444",
  fontSize: "12px",
  marginTop: "4px",
};

const ToggleBtn = ({ label, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      flex: 1,
      padding: "9px 12px",
      border: `1px solid ${active ? "#C0335A" : "#E0E0E0"}`,
      borderRadius: "8px",
      background: active ? "#FDE8EF" : "#fff",
      color: active ? "#C0335A" : "#374151",
      fontWeight: active ? "600" : "400",
      fontSize: "14px",
      cursor: "pointer",
      transition: "all 0.15s",
    }}
  >
    {label}
  </button>
);

const buildInitialForm = (isEditMode, initialData) => {
  if (isEditMode && initialData) {
    let templateVariables = [];
    try {
      templateVariables =
        typeof initialData.whatsapp_template_params === "string"
          ? JSON.parse(initialData.whatsapp_template_params || "[]")
          : initialData.whatsapp_template_params || [];
    } catch {
      templateVariables = [];
    }
    return {
      name: initialData.name || "",
      channel: initialData.channel || "SMS",
      body: initialData.body || "",
      templateId: initialData.whatsapp_template_id ? String(initialData.whatsapp_template_id) : "",
      templateVariables,
      audienceType: initialData.audience_type || "ALL_CUSTOMERS",
      audienceFileName: initialData.audience_file_name || "",
      audienceFile: null,
      sendMode: initialData.send_mode === "SCHEDULED" ? "SCHEDULED" : "NOW",
      scheduledAt: initialData.scheduled_at ? new Date(initialData.scheduled_at) : null,
    };
  }
  return {
    name: "",
    channel: "SMS",
    body: "",
    templateId: "",
    templateVariables: [],
    audienceType: "ALL_CUSTOMERS",
    audienceFileName: "",
    audienceFile: null,
    sendMode: "NOW",
    scheduledAt: null,
  };
};

const readFileAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const CreateCampaignDrawer = ({ onClose, onSuccess, isEditMode = false, initialData = null }) => {
  const [form, setForm] = useState(() => buildInitialForm(isEditMode, initialData));
  const [errors, setErrors] = useState({});
  const [isDirty, setIsDirty] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [audienceCount, setAudienceCount] = useState(null);
  const [fileParsing, setFileParsing] = useState(false);

  useEffect(() => {
    getVendorTemplates()
      .then((res) => setTemplates(res.data || []))
      .catch(() => {})
      .finally(() => setTemplatesLoading(false));
  }, []);

  useEffect(() => {
    if (form.audienceType !== "ALL_CUSTOMERS") return;
    let cancelled = false;
    getAudiencePreviewCount()
      .then((res) => {
        if (!cancelled && res.success) setAudienceCount(res.data?.count ?? 0);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [form.audienceType]);

  const setField = (key, val) => {
    setIsDirty(true);
    setForm((f) => ({ ...f, [key]: val }));
    setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const handleTemplateChange = (id) => {
    setIsDirty(true);
    const tpl = templates.find((t) => String(t.id) === String(id));
    const count = tpl?.variable_count || 0;
    setForm((f) => ({
      ...f,
      templateId: id,
      templateVariables:
        count > 0 ? Array.from({ length: count }, (_, i) => f.templateVariables[i] || "") : f.templateVariables,
    }));
    setErrors((e) => ({ ...e, templateId: undefined }));
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsDirty(true);
    setFileParsing(true);
    try {
      const arrayBuffer = await file.arrayBuffer();
      const wb = XLSX.read(arrayBuffer, { type: "array" });
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: false });
      let phones = rows.map((r) => (r && r[0] != null ? String(r[0]).trim() : "")).filter((v) => v !== "");
      if (phones.length && /[a-zA-Z]/.test(phones[0]) && !/\d{5,}/.test(phones[0])) {
        phones = phones.slice(1); // drop a header row like "phone_number"
      }
      const base64 = await readFileAsDataUrl(file);
      setForm((f) => ({
        ...f,
        audienceFile: { base64, fileName: file.name, phoneNumbers: phones },
        audienceFileName: file.name,
      }));
      setErrors((e) => ({ ...e, audienceFile: undefined }));
    } catch (err) {
      toast.error("Could not read that file. Upload a CSV or Excel file with one phone number per row.");
    } finally {
      setFileParsing(false);
    }
  };

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = "Campaign name is required";
    if (form.channel === "SMS" && !form.body.trim()) errs.body = "Message body is required";
    if (form.channel === "WHATSAPP" && !form.templateId) errs.templateId = "Select a WhatsApp template";
    if (form.audienceType === "UPLOADED_LIST" && !form.audienceFile && !form.audienceFileName) {
      errs.audienceFile = "Upload a list of phone numbers";
    }
    if (form.sendMode === "SCHEDULED") {
      if (!form.scheduledAt) errs.scheduledAt = "Select a date and time";
      else if (form.scheduledAt.getTime() <= Date.now()) errs.scheduledAt = "Must be in the future";
    }
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    try {
      setSubmitting(true);

      const payload = {
        name: form.name.trim(),
        channel: form.channel,
        audience_type: form.audienceType,
        send_mode: form.sendMode,
      };
      if (form.channel === "SMS") {
        payload.body = form.body.trim();
      } else {
        const tpl = templates.find((t) => String(t.id) === String(form.templateId));
        payload.whatsapp_template_id = form.templateId;
        payload.whatsapp_template_name = tpl?.template || "";
        payload.whatsapp_template_params = form.templateVariables;
      }
      let scheduledAtStr = null;
      if (form.sendMode === "SCHEDULED") {
        const d = form.scheduledAt;
        scheduledAtStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}:00`;
        payload.scheduled_at = scheduledAtStr;
      }
      if (isEditMode) payload.id = initialData.id;

      const saveRes = await saveCampaign(payload);
      if (!saveRes.success) throw new Error(saveRes.message || "Failed to save campaign");
      const campaignId = saveRes.data.campaign_id;

      if (form.audienceType === "UPLOADED_LIST" && form.audienceFile) {
        const uploadRes = await uploadAudienceFile({
          campaign_id: campaignId,
          file_name: form.audienceFile.fileName,
          file: form.audienceFile.base64,
          phone_numbers: form.audienceFile.phoneNumbers,
        });
        if (!uploadRes.success) throw new Error(uploadRes.message || "Failed to upload audience file");
      }

      if (form.sendMode === "SCHEDULED") {
        const schedRes = await scheduleCampaign({ campaign_id: campaignId, scheduled_at: scheduledAtStr });
        if (!schedRes.success) throw new Error(schedRes.message || "Failed to schedule campaign");
        toast.success("Campaign scheduled");
      } else {
        const sendRes = await sendCampaignNow(campaignId);
        if (!sendRes.success) throw new Error(sendRes.message || "Failed to send campaign");
        toast.success("Campaign sent");
      }

      onSuccess?.();
      onClose();
    } catch (err) {
      toast.error(err?.message || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  const handleBackdropClick = () => {
    if (isDirty) {
      if (!window.confirm("You have unsaved changes. Close anyway?")) return;
    }
    onClose();
  };

  const selectedTemplate = templates.find((t) => String(t.id) === String(form.templateId));
  const templateLabels = selectedTemplate?.variable_labels
    ? selectedTemplate.variable_labels.split(",").map((s) => s.trim())
    : null;
  const templateFixedCount = selectedTemplate?.variable_count || 0;

  const submitLabel = submitting
    ? form.sendMode === "SCHEDULED"
      ? "Scheduling..."
      : "Sending..."
    : form.sendMode === "SCHEDULED"
    ? "Schedule Campaign"
    : "Send Campaign Now";

  return (
    <>
      <div
        onClick={handleBackdropClick}
        style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", zIndex: 1040 }}
      />

      <div
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          bottom: 0,
          width: "min(520px, 100vw)",
          background: "#fff",
          boxShadow: "-4px 0 24px rgba(0,0,0,0.12)",
          zIndex: 1050,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "20px 24px",
            borderBottom: "1px solid #F3F4F6",
            position: "sticky",
            top: 0,
            background: "#fff",
            zIndex: 1,
          }}
        >
          <h2 style={{ margin: 0, fontSize: "18px", fontWeight: "700", color: "#111827" }}>
            {isEditMode ? "Edit Campaign" : "Create Campaign"}
          </h2>
          <button
            onClick={handleBackdropClick}
            style={{
              background: "none",
              border: "1px solid #E5E7EB",
              borderRadius: "8px",
              width: "36px",
              height: "36px",
              fontSize: "18px",
              cursor: "pointer",
              color: "#6B7280",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", minHeight: 0 }}>
          <div style={{ flex: 1, overflowY: "auto", padding: "24px", display: "flex", flexDirection: "column", gap: "20px" }}>
            {/* Name */}
            <div>
              <label style={labelStyle}>Campaign Name</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setField("name", e.target.value)}
                placeholder="e.g. August Promo Blast"
                style={inputStyle}
              />
              {errors.name && <div style={errorStyle}>{errors.name}</div>}
            </div>

            {/* Channel */}
            <div>
              <label style={labelStyle}>Channel</label>
              <div style={{ display: "flex", gap: "8px" }}>
                <ToggleBtn label="SMS" active={form.channel === "SMS"} onClick={() => setField("channel", "SMS")} />
                <ToggleBtn label="WhatsApp" active={form.channel === "WHATSAPP"} onClick={() => setField("channel", "WHATSAPP")} />
              </div>
            </div>

            {/* Audience */}
            <div>
              <label style={labelStyle}>Audience</label>
              <div style={{ display: "flex", gap: "8px", marginBottom: "12px" }}>
                <ToggleBtn
                  label="All My Customers"
                  active={form.audienceType === "ALL_CUSTOMERS"}
                  onClick={() => setField("audienceType", "ALL_CUSTOMERS")}
                />
                <ToggleBtn
                  label="Upload a List"
                  active={form.audienceType === "UPLOADED_LIST"}
                  onClick={() => setField("audienceType", "UPLOADED_LIST")}
                />
              </div>

              {form.audienceType === "ALL_CUSTOMERS" ? (
                <div style={{ padding: "10px 14px", background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: "8px", fontSize: "13px", color: "#15803D" }}>
                  {audienceCount === null ? "Calculating audience size…" : `This will reach ${audienceCount} customer${audienceCount === 1 ? "" : "s"}`}
                </div>
              ) : (
                <>
                  <input
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={handleFileChange}
                    style={{ fontSize: "13px" }}
                  />
                  {fileParsing && <div style={{ fontSize: "13px", color: "#6B7280", marginTop: "6px" }}>Reading file…</div>}
                  {form.audienceFile && (
                    <div style={{ marginTop: "8px", padding: "10px 14px", background: "#EFF6FF", border: "1px solid #BFDBFE", borderRadius: "8px", fontSize: "13px", color: "#1D4ED8" }}>
                      Detected {form.audienceFile.phoneNumbers.length} phone number{form.audienceFile.phoneNumbers.length === 1 ? "" : "s"} in {form.audienceFile.fileName}
                    </div>
                  )}
                  {!form.audienceFile && form.audienceFileName && (
                    <div style={{ marginTop: "8px", fontSize: "13px", color: "#6B7280" }}>
                      Currently using: {form.audienceFileName} (choose a new file to replace it)
                    </div>
                  )}
                  {errors.audienceFile && <div style={errorStyle}>{errors.audienceFile}</div>}
                </>
              )}
            </div>

            {/* SMS body */}
            {form.channel === "SMS" && (
              <div>
                <label style={labelStyle}>Message</label>
                <textarea
                  value={form.body}
                  onChange={(e) => setField("body", e.target.value)}
                  placeholder="Type your SMS message..."
                  rows={5}
                  style={{ ...inputStyle, height: "auto", padding: "10px 12px", resize: "vertical" }}
                />
                <div style={{ fontSize: "12px", color: "#9CA3AF", marginTop: "4px" }}>{form.body.length} characters</div>
                {errors.body && <div style={errorStyle}>{errors.body}</div>}
              </div>
            )}

            {/* WhatsApp template */}
            {form.channel === "WHATSAPP" && (
              <div>
                <label style={labelStyle}>WhatsApp Template</label>
                {templatesLoading ? (
                  <div style={{ fontSize: "13px", color: "#6B7280" }}>Loading templates…</div>
                ) : templates.length === 0 ? (
                  <div style={{ padding: "12px 14px", background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: "8px", fontSize: "13px", color: "#92400E" }}>
                    No approved WhatsApp templates yet.{" "}
                    <a href="/channel" style={{ color: "#C0335A", fontWeight: "600" }}>
                      Request one in Channels
                    </a>
                    .
                  </div>
                ) : (
                  <>
                    <select value={form.templateId} onChange={(e) => handleTemplateChange(e.target.value)} style={selectStyle}>
                      <option value="">-- Select Template --</option>
                      {templates.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.template}
                          {t.description ? ` — ${t.description}` : ""}
                        </option>
                      ))}
                    </select>
                    {errors.templateId && <div style={errorStyle}>{errors.templateId}</div>}
                  </>
                )}

                {form.templateId && (
                  <div style={{ marginTop: "12px", display: "flex", flexDirection: "column", gap: "10px" }}>
                    {form.templateVariables.map((val, i) => (
                      <div key={i} style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                        <input
                          type="text"
                          value={val}
                          onChange={(e) => {
                            const next = [...form.templateVariables];
                            next[i] = e.target.value;
                            setField("templateVariables", next);
                          }}
                          placeholder={templateLabels?.[i] || `Variable ${i + 1}`}
                          style={inputStyle}
                        />
                        {templateFixedCount === 0 && (
                          <button
                            type="button"
                            onClick={() => setField("templateVariables", form.templateVariables.filter((_, idx) => idx !== i))}
                            style={{ background: "none", border: "1px solid #E5E7EB", borderRadius: "8px", width: "36px", height: "36px", cursor: "pointer", color: "#6B7280" }}
                          >
                            ✕
                          </button>
                        )}
                      </div>
                    ))}
                    {templateFixedCount === 0 && form.templateVariables.length < 6 && (
                      <button
                        type="button"
                        onClick={() => setField("templateVariables", [...form.templateVariables, ""])}
                        style={{ alignSelf: "flex-start", background: "#FDE8EF", border: "1px solid #C0335A", color: "#C0335A", borderRadius: "8px", padding: "6px 12px", fontSize: "13px", fontWeight: "600", cursor: "pointer" }}
                      >
                        + Add variable
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Send mode */}
            <div>
              <label style={labelStyle}>Send</label>
              <div style={{ display: "flex", gap: "8px", marginBottom: "12px" }}>
                <ToggleBtn label="Send Now" active={form.sendMode === "NOW"} onClick={() => setField("sendMode", "NOW")} />
                <ToggleBtn label="Schedule for later" active={form.sendMode === "SCHEDULED"} onClick={() => setField("sendMode", "SCHEDULED")} />
              </div>
              {form.sendMode === "SCHEDULED" && (
                <div>
                  <DatePicker
                    selected={form.scheduledAt}
                    onChange={(date) => setField("scheduledAt", date)}
                    showTimeSelect
                    dateFormat="d MMMM yyyy, h:mm aa"
                    minDate={new Date()}
                    placeholderText="Select date and time"
                    customInput={<input style={inputStyle} />}
                  />
                  {errors.scheduledAt && <div style={errorStyle}>{errors.scheduledAt}</div>}
                </div>
              )}
            </div>
          </div>

          <div
            style={{
              padding: "16px 24px",
              borderTop: "1px solid #F3F4F6",
              background: "#fff",
              flexShrink: 0,
              paddingBottom: "max(16px, env(safe-area-inset-bottom))",
            }}
          >
            <button
              type="submit"
              disabled={submitting}
              style={{
                width: "100%",
                padding: "14px",
                background: submitting ? "#e89fb7" : "#C0335A",
                color: "#fff",
                border: "none",
                borderRadius: "9999px",
                fontSize: "15px",
                fontWeight: "600",
                cursor: submitting ? "not-allowed" : "pointer",
                transition: "background 0.2s",
              }}
            >
              {submitLabel}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default CreateCampaignDrawer;
